import { useMutation, useQueryClient } from "@tanstack/react-query";

import { proposalQueryKeys } from "@/lib/query/query-keys";

import {
  createProposal,
  deleteProposal,
  updateProposal,
  useProposalsApi,
} from "./api";
import type {
  CreateProposalInput,
  ProposalsQueryResult,
  UpdateProposalInput,
} from "./types";

export function useCreateProposal() {
  const { api, ensureReady } = useProposalsApi({
    enabled: true,
    debugKey: "useCreateProposal",
    requireContextReady: false,
  });
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateProposalInput) => {
      ensureReady();
      return createProposal(api, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.lists(),
        refetchType: "active",
      });
    },
  });
}

export function useUpdateProposal() {
  const { api, ensureReady } = useProposalsApi({
    enabled: true,
    debugKey: "useUpdateProposal",
    requireContextReady: false,
  });
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateProposalInput;
    }) => {
      ensureReady();
      return updateProposal(api, id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.lists(),
        refetchType: "active",
      });
    },
  });
}

export function useDeleteProposal() {
  const { api, ensureReady } = useProposalsApi({
    enabled: true,
    debugKey: "useDeleteProposal",
    requireContextReady: false,
  });
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      ensureReady();
      await deleteProposal(api, id);
      return id;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: proposalQueryKeys.all });
      const previous = queryClient.getQueriesData<ProposalsQueryResult>({
        queryKey: proposalQueryKeys.lists(),
      });
      queryClient.setQueriesData<ProposalsQueryResult>(
        { queryKey: proposalQueryKeys.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter((proposal) => proposal.proposal_id !== id),
            pagination: {
              ...old.pagination,
              total: Math.max(0, old.pagination.total - 1),
            },
          };
        },
      );
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          if (data) queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.all,
        refetchType: "active",
      });
    },
  });
}
